import { MouseEvent, useState, useEffect } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { useForm } from 'react-hook-form';

import { useAppDispatch } from '../../app/hooks';
import {
  createScopeAction,
  scope_field_options,
} from './scopeSlice';
import { Scope } from '../../types';

const initialState: Required<Pick<Scope, 'scope_id'|'name'|'title'|'description'>> = {
  scope_id: '',
  name: '',
  title: '',
  description: '',
};

type Props = {
  tenant_id: string;
  successCallback: (scope_name: string) => void;
  dismissCallback: () => void;
};

export function ScopeCreateForm({ tenant_id, successCallback, dismissCallback }: Props) {
  const dispatch = useAppDispatch();
  const [api_error, setApiError] = useState<SerializedError|undefined>();
  const { handleSubmit, register, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      ...initialState,
      scope_id: `${tenant_id}/`,
    },
  });
  const scope_name = watch('name');

  useEffect(() => {
    setValue('scope_id', `${tenant_id}/${scope_name}`);
  }, [scope_name, tenant_id, setValue]);

  const handleCreate = async(data: Scope) => {
    let result = await dispatch(createScopeAction({
      tenant_id,
      body: data,
    }));
    if (createScopeAction.fulfilled.match(result)) {
      successCallback(scope_name);
    } else {
      setApiError(result.error);
      const subscription = watch(() => {
        setApiError(undefined);
        subscription.unsubscribe();
      });
    }
  };
  const handleCancel = (event: MouseEvent) => {
    event.preventDefault();
    dismissCallback();
  };
  return (
    <form onSubmit={handleSubmit(handleCreate)}>
      <fieldset>
        <div className="mb-3 row">
          <label htmlFor="role_id" className="col-sm-3 col-form-label">ID</label>
          <div className="col-sm-6">
            <input disabled type="text" id="scope_id" className="form-control-plaintext"
              { ...register('scope_id') }
            />
          </div>
        </div>
        <div className="mb-3 row">
          <label htmlFor="scope_name" className="col-sm-3 col-form-label">Name</label>
          <div className="col-sm-6">
            <input type="text" id="scope_name" className="form-control"
              { ...register('name', scope_field_options.name) }
            />
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.name?.message}</span>
        </div>
        <div className="mb-3 row">
          <label htmlFor="scope_title" className="col-sm-3 col-form-label">Title</label>
          <div className="col-sm-6">
            <input id="scope_title" className="form-control"
              { ...register('title', scope_field_options.title) }
            />
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.title?.message}</span>
        </div>
        <div className="mb-3 row">
          <label htmlFor="scope_description" className="col-sm-3 col-form-label">Description</label>
          <div className="col-sm-6">
            <textarea id="scope_description" className="form-control"
              { ...register('description', scope_field_options.description) }
            >
            </textarea>
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.description?.message}</span>
        </div>
        <div className="d-flex">
          <span className="text-danger">{api_error?.message ?? api_error?.name}</span>
        </div>
        <div className="d-flex my-3 justify-content-center">
          <button type="submit" className="btn btn-primary mx-3">Create</button>
          <button className="btn btn-light mx-1" onClick={(e) => handleCancel(e)}>Cancel</button>
        </div>
      </fieldset>
    </form>
  );
}
